import type { ApolloError } from '@apollo/client';
import { Link } from 'react-router';

import { ERROR_CODES, apiErrorMessage, firstErrorCode } from '@/core/api';
import {
  INVITATION_BATCH_EXPIRED_NEXT_STEP,
  getDefaultErrorMessage,
} from '@/core/apiErrorMessages';

import Banner from './Banner';

interface LoadErrorProps {
  /** The read's error; none when the api found no batch for the token. */
  error?: ApolloError;
  /** From the URL, for the link back. */
  expertRequestId: string;
}

const LOAD_FAILED = "We couldn't load these invitations. Refresh the page to try again.";

export default function LoadError({ error, expertRequestId }: LoadErrorProps): JSX.Element {
  const code = firstErrorCode(error);
  const expired = code === ERROR_CODES.INVITATION_BATCH_EXPIRED;
  let title: string | undefined = "Couldn't load these invitations";
  let text = apiErrorMessage(error, LOAD_FAILED);
  if (code === ERROR_CODES.INVITATION_BATCH_FORBIDDEN) {
    title = "These invitations aren't yours to send";
  } else if (expired) {
    // The banner's text says it expired; the line under the title says what next.
    title = undefined;
  } else if (!error || code === ERROR_CODES.INVITATION_BATCH_NOT_FOUND) {
    title = 'Invitation link not found';
    text = getDefaultErrorMessage(ERROR_CODES.INVITATION_BATCH_NOT_FOUND) ?? LOAD_FAILED;
  }

  return (
    <div className="flex flex-col gap-24 py-16">
      <header className="flex flex-col gap-8">
        <h1 className="text-on-surface hd-3">Send invitations</h1>
        {expired ? (
          <p className="m-[0] text-on-surface body-1">{INVITATION_BATCH_EXPIRED_NEXT_STEP}</p>
        ) : null}
      </header>
      <Banner tone={expired ? 'warning' : 'error'} title={title}>
        {text}
      </Banner>
      <Link
        to={`/expert_request/${expertRequestId}`}
        className="font-semibold text-brand-primary body-1 hover:underline"
      >
        Back to the expert request
      </Link>
    </div>
  );
}
